import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  type TClientSecurityUpdateDto,
  type TSecurityCreateDto
} from '@/components/App/Security/types/security.types';
import { type TSDFilesMapItem } from '@/components/Shared/widgets/types/serviceDesk.type';
import { securityApi } from '@/integrations/app.api/security.api';
import {
  securityDetailedMapperFromApi,
  securityMapperFromApi
} from '@/services/mappers/securityMapper';

import { type TSecurityStore } from '../types/store';
import { createAsyncThunk } from './create-async-thunk';

const initialState: TSecurityStore = {
  filesMap: {},
  isLoadingAll: false,
  newSecurityCount: 0,
  securityRequest: null,
  securityRequests: [],
  total: 0
};

export type TSecurityFilters = {
  limit: string;
  offset: string;
  searchTerm?: string;
  statusId?: string[];
};

export const startBadgeSecurity = createAsyncThunk(
  'security/startBadgeSecurity',
  async () => await securityApi.startBadgeSecurity()
);

export const getSecurityRequests = createAsyncThunk(
  'security/getSecurityRequests',
  async (filters: TSecurityFilters) =>
    await securityApi.getSecurityRequests(filters)
);

export const getSecurityRequest = createAsyncThunk(
  'security/getSecurityRequest',
  async (requestId: string) => await securityApi.getSecurityRequest(requestId)
);

export const createSecurityRequest = createAsyncThunk(
  'serviceDesk/createSecurityRequest',
  async (data: TSecurityCreateDto) => {
    return await securityApi.createSecurityRequest(data);
  }
);

export const updateStatusSecurityRequest = createAsyncThunk(
  'security/updateStatusSecurityRequest',
  async (data: TClientSecurityUpdateDto & { uuid: string }) => {
    const { uuid, ...params } = data;
    return await securityApi.updateStatusSecurityRequest(uuid, params);
  }
);

export const securitySlice = createSlice({
  name: 'securitySlice',
  initialState,
  reducers: {
    resetSecurityRequest: (state) => {
      state.securityRequest = null;
      state.filesMap = {};
    },
    initializeFilesMap: (
      state,
      action: PayloadAction<{ files: { uuid: string; name: string } }[]>
    ) => {
      state.filesMap = {
        ...state.filesMap,
        ...action.payload.reduce(
          (acc, { files: { uuid, name } }) => {
            acc[uuid] = {
              state: 'initial',
              base64: '',
              name
            };
            return acc;
          },
          {} as Record<string, TSDFilesMapItem>
        )
      };
    },
    updateFilesMap: (
      state,
      action: PayloadAction<{ uuid: string; mapItem: TSDFilesMapItem }>
    ) => {
      state.filesMap[action.payload.uuid] = action.payload.mapItem;
    },
    updateBadgeCount: (state, action: PayloadAction<number>) => {
      state.newSecurityCount = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSecurityRequests.pending, (state) => {
        state.isLoadingAll = true;
      })
      .addCase(getSecurityRequests.rejected, (state) => {
        state.isLoadingAll = false;
        state.securityRequests = [];
      })
      .addCase(getSecurityRequests.fulfilled, (state, action) => {
        state.isLoadingAll = false;
        state.securityRequests = action.payload[0].data.map(
          securityMapperFromApi
        );
        state.total = action.payload[0].total;
      })
      .addCase(getSecurityRequest.rejected, (state) => {
        state.securityRequest = null;
      })
      .addCase(getSecurityRequest.fulfilled, (state, action) => {
        state.securityRequest = securityDetailedMapperFromApi(action.payload);
      })
      .addCase(getSecurityRequest.pending, (state) => {
        state.securityRequest = null;
      })
      .addCase(startBadgeSecurity.fulfilled, (state, action) => {
        state.newSecurityCount = action.payload || 0;
      })
      .addCase(startBadgeSecurity.rejected, (state) => {
        state.newSecurityCount = 0;
      });
  }
});

export const {
  resetSecurityRequest,
  initializeFilesMap,
  updateFilesMap,
  updateBadgeCount
} = securitySlice.actions;
